import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import UserRoleFilter, {
  canFilterBySelf,
} from "@/components/molecules/UserRoleFilter";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import useLoadOptions from "@/hooks/useLoadOptions";
import ActivitySector from "@/interfaces/ActivitySector";
import CompanyStatus from "@/interfaces/CompanyStatus";
import { MeetingFilters } from "@/interfaces/Meeting";
import Opco from "@/interfaces/Opco";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { FC } from "react";
import { Badge, Button, Form, Offcanvas } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MultiValue } from "react-select";

/**
 * Panneau « Filtres » du calendrier commercial. Le calendrier montage a le sien
 * (MontageCalendarFilters) et ne partage que le bouton.
 */
export type CalendarFilters = Omit<MeetingFilters, "date">;

/** Valeurs posées par « Réinitialiser ». */
export const EMPTY_CALENDAR_FILTERS: CalendarFilters = {
  aes: [],
  sdrs: [],
  status: [],
  companyCourses: [],
  unknownStatus: false,
  activitySectors: [],
  opcos: [],
  reminderDatetime: null,
  siret: "",
  city: "",
  zipCode: "",
  name: "",
  phone: "",
};

export const countActiveCalendarFilters = (filters: CalendarFilters) =>
  (filters.aes?.length || 0) +
  (filters.sdrs?.length || 0) +
  (filters.status?.length || 0) +
  (filters.companyCourses?.length || 0) +
  (filters.activitySectors?.length || 0) +
  (filters.opcos?.length || 0) +
  (filters.unknownStatus ? 1 : 0) +
  (filters.reminderDatetime ? 1 : 0) +
  (filters.siret ? 1 : 0) +
  (filters.city ? 1 : 0) +
  (filters.zipCode ? 1 : 0) +
  (filters.name ? 1 : 0) +
  (filters.phone ? 1 : 0);

/**
 * Paramètres envoyés à l'API. Les champs texte attendent 500 ms de pause dans
 * la frappe avant de partir, pour ne pas lancer une requête par caractère.
 */
export const useCalendarFilterParams = (filters: CalendarFilters) => {
  const siret = useDebouncedValue(filters.siret, 500);
  const city = useDebouncedValue(filters.city, 500);
  const zipCode = useDebouncedValue(filters.zipCode, 500);
  const name = useDebouncedValue(filters.name, 500);
  const phone = useDebouncedValue(filters.phone, 500);

  return {
    ...(filters.aes?.length && {
      aeIds: filters.aes.map((user) => user.id),
    }),
    ...(filters.sdrs?.length && {
      sdrIds: filters.sdrs.map((user) => user.id),
    }),
    ...(filters.status?.length && {
      statusIds: filters.status.map((element) => element.id),
    }),
    ...(filters.companyCourses?.length && {
      companyCourseIds: filters.companyCourses.map((element) => element.id),
    }),
    ...(filters.activitySectors?.length && {
      activitySectorIds: filters.activitySectors.map((element) => element.id),
    }),
    ...(filters.opcos?.length && {
      opcoIds: filters.opcos.map((element) => element.id),
    }),
    ...(filters.unknownStatus && { unknownStatus: true }),
    ...(filters.reminderDatetime && {
      reminderDatetime: filters.reminderDatetime,
    }),
    ...(siret && { siret }),
    ...(city && { city }),
    ...(zipCode && { zipCode }),
    ...(name && { name }),
    ...(phone && { phone }),
  };
};

export const CalendarFiltersButton: FC<{
  count: number;
  onClick: () => void;
}> = ({ count, onClick }) => (
  <div className="position-relative me-2">
    <IconButton
      variant="nolina-default"
      size="sm"
      icon={faFilter}
      transform="shrink-4"
      iconAlign="middle"
      onClick={onClick}
    >
      <span className="ms-1 d-none d-sm-inline-block">Filtres</span>
    </IconButton>
    {count > 0 && (
      <Badge
        bg="danger"
        pill
        className="position-absolute"
        style={{
          top: "-8px",
          right: "-8px",
          fontSize: "0.65rem",
          minWidth: "18px",
          height: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {count}
      </Badge>
    )}
  </div>
);

interface CalendarFiltersPanelProps {
  show: boolean;
  onHide: () => void;
  filters: CalendarFilters;
  onFiltersChange: (changes: Partial<CalendarFilters>) => void;
  /** Affiche les sélecteurs Commercial et Télépro. Sans cela, chacun reste
   *  affiché à qui a le rôle correspondant, pour se choisir soi-même. */
  showUserFilters: boolean;
  /** `all` : tous les utilisateurs. `pool` : l'entourage seulement
   *  (/users/calendar-filterable). */
  userOptions: "all" | "pool";
}

export const CalendarFiltersPanel: FC<CalendarFiltersPanelProps> = ({
  show,
  onHide,
  filters,
  onFiltersChange,
  showUserFilters,
  userOptions,
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const {
    loadCompanyStatusesOptions,
    loadActivitySectors,
    loadOpcosOptions,
    loadCompaniesCoursesOptions,
  } = useLoadOptions();

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="dark__bg-card-dark"
    >
      <Offcanvas.Header closeButton className="bg-light">
        <h6 className="fs-0 mb-0 fw-semi-bold">Filtres</h6>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Form.Group className="mt-n2">
            <Flex className="flex-between-center">
              <Form.Label className="mb-1 fs--1">Statuts</Form.Label>
            </Flex>
            <SelectPaginateAsync
              loadOptions={loadCompanyStatusesOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              value={filters.status}
              onChange={(status: MultiValue<CompanyStatus>) =>
                onFiltersChange({ status })
              }
              isMulti
              isClearable={false}
            />
            <Form.Check
              checked={!!filters.unknownStatus}
              onChange={() =>
                onFiltersChange({ unknownStatus: !filters.unknownStatus })
              }
              label="Inconnu"
              className="my-2"
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Formation liée</Form.Label>
            <SelectPaginateAsync
              loadOptions={loadCompaniesCoursesOptions}
              getOptionLabel={(option) =>
                `${option.course?.name ?? "Formation"} — ${
                  option.company?.name ?? ""
                }`
              }
              getOptionValue={(option) => option.id.toString()}
              value={filters.companyCourses}
              onChange={(companyCourses) => onFiltersChange({ companyCourses })}
              isMulti
              isClearable
            />
          </Form.Group>
          {(showUserFilters || canFilterBySelf(currentUser, "AE")) && (
            <UserRoleFilter
              role="AE"
              source={userOptions}
              value={filters.aes}
              onChange={(aes) => onFiltersChange({ aes })}
            />
          )}
          {(showUserFilters || canFilterBySelf(currentUser, "SDR")) && (
            <UserRoleFilter
              role="SDR"
              source={userOptions}
              value={filters.sdrs}
              onChange={(sdrs) => onFiltersChange({ sdrs })}
            />
          )}
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Secteur d'activité :</Form.Label>
            <SelectPaginateAsync
              loadOptions={loadActivitySectors}
              getOptionLabel={(option) =>
                `${option.opco?.name} - ${option.name}`
              }
              getOptionValue={(option) => option.id.toString()}
              value={filters.activitySectors}
              isClearable
              isMulti
              onChange={(activitySectors: MultiValue<ActivitySector>) =>
                onFiltersChange({ activitySectors })
              }
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">OPCO :</Form.Label>
            <SelectPaginateAsync
              loadOptions={loadOpcosOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              value={filters.opcos}
              isClearable
              isMulti
              onChange={(opcos: MultiValue<Opco>) => onFiltersChange({ opcos })}
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Rappel avant le :</Form.Label>
            <Form.Control
              type="datetime-local"
              value={filters.reminderDatetime || ""}
              onChange={(e) =>
                onFiltersChange({ reminderDatetime: e.target.value || null })
              }
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Siret :</Form.Label>
            <Form.Control
              type="text"
              value={filters.siret || ""}
              onChange={(e) =>
                onFiltersChange({ siret: e.target.value || null })
              }
              placeholder="Rechercher par siret..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Ville :</Form.Label>
            <Form.Control
              type="text"
              value={filters.city || ""}
              onChange={(e) =>
                onFiltersChange({ city: e.target.value || null })
              }
              placeholder="Rechercher par ville..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Code postal :</Form.Label>
            <Form.Control
              type="text"
              value={filters.zipCode || ""}
              onChange={(e) =>
                onFiltersChange({ zipCode: e.target.value || null })
              }
              placeholder="Rechercher par code postal..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Nom :</Form.Label>
            <Form.Control
              type="text"
              value={filters.name || ""}
              onChange={(e) =>
                onFiltersChange({ name: e.target.value || null })
              }
              placeholder="Rechercher par nom..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Téléphone :</Form.Label>
            <Form.Control
              type="text"
              value={filters.phone || ""}
              onChange={(e) =>
                onFiltersChange({ phone: e.target.value || null })
              }
              placeholder="Rechercher par téléphone..."
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onFiltersChange(EMPTY_CALENDAR_FILTERS)}
            >
              Réinitialiser
            </Button>
          </Form.Group>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};
